import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { mapFetchResultData } from '@x/common/graph';
import { ShopUpdateUserPasswordInput } from '@x/schemas/ecommerce';
import { Observable } from 'rxjs';
import { ShopCheckoutOrderGQL } from './gql/shop-checkout-order.gql.generated';
import { ShopUserOrderRecreateGQL } from './gql/shop-order.gql.generated';
import {
  ForgotPasswordGQL,
  ResetPasswordGQL,
  ShopArchiveUserPaymentMethodGQL,
  ShopCancelUserPaymentMethodGQL,
  ShopCreateUserAddressGQL,
  ShopCreateUserPaymentMethodGQL,
  ShopDeleteUserAddressGQL,
  ShopRefreshUserGQL,
  ShopUpdateUserAddressGQL,
  ShopUpdateUserGQL,
  ShopUpdateUserPasswordGQL,
  ShopUserAddressesGQL,
  ShopUserAuthGQL,
  ShopUserDeclarationGQL,
  ShopUserLoginGQL,
  ShopUserOrderItemGQL,
  ShopUserOrdersGQL,
  ShopUserPaymentMethodGQL,
  ShopUserRefreshPaymentMethodsGQL,
  ShopUserRegistrationGQL,
} from './gql/shop-user.gql.generated';
import { AUTHORIZATION_HEADER_PROVIDER, AuthorizationHeaderProvider } from './types/shop-auth';
import { IShopCollection } from './types/shop-collection';
import { IShopOrderId } from './types/shop-order';
import {
  IForgotPassword,
  IForgotPasswordMutationVariables,
  IResetPassword,
  IResetPasswordMutation,
  IShopAddress,
  IShopArchiveUserPayment,
  IShopArchiveUserPaymentMethodVariables,
  IShopAuthToken,
  IShopAuthUser,
  IShopCancelUserPaymentMethodVariables,
  IShopCreateUserAddressVariables,
  IShopCreateUserPaymentMethodVariables,
  IShopDeleteUserAddressVariables,
  IShopRefreshUserMutationVariables,
  IShopUpdateUserAddressVariables,
  IShopUpdateUserMutationVariables,
  IShopUserAddress,
  IShopUserLoginVariables,
  IShopUserOrder,
  IShopUserOrderRecreate,
  IShopUserOrdersQuery,
  IShopUserPaymentMethod,
  IShopUserPaymentMethodVariables,
  IShopUserRegistrationVariables,
  TShopUserAgreement,
  TShopUserDeclarationMutationVariables,
  TShopUserOrderDetail,
  TShopUserOrderItem,
  TShopUserOrderItemQuery,
  TShopUserOrderRecreateMutation,
} from './types/shop-user';

@Injectable()
export class ShopUserService {
  constructor(
    @Inject(AUTHORIZATION_HEADER_PROVIDER)
    private authHeader: AuthorizationHeaderProvider,
    private shopUserRegistrationGQL: ShopUserRegistrationGQL,
    private shopUserLoginGQL: ShopUserLoginGQL,
    private shopUserAuthGQL: ShopUserAuthGQL,
    private shopCreateUserAddressGQL: ShopCreateUserAddressGQL,
    private shopUpdateUserAddressGQL: ShopUpdateUserAddressGQL,
    private shopDeleteUserAddressGQL: ShopDeleteUserAddressGQL,
    private forgotPasswordGQL: ForgotPasswordGQL,
    private resetPasswordGQL: ResetPasswordGQL,
    private shopUpdateUserGQL: ShopUpdateUserGQL,
    private shopUserOrdersGQL: ShopUserOrdersGQL,
    private shopCheckoutOrderGQL: ShopCheckoutOrderGQL,
    private shopUpdateUserPasswordGQL: ShopUpdateUserPasswordGQL,
    private shopArchiveUserPaymentMethodGQL: ShopArchiveUserPaymentMethodGQL,
    private shopCreateUserPaymentMethodGQL: ShopCreateUserPaymentMethodGQL,
    private shopCancelUserPaymentMethodGQL: ShopCancelUserPaymentMethodGQL,
    private shopUserPaymentMethodGQL: ShopUserPaymentMethodGQL,
    private shopRefreshUserGQL: ShopRefreshUserGQL,
    private shopUserAddressesGQL: ShopUserAddressesGQL,
    private shopUserRefreshPaymentMethodsGQL: ShopUserRefreshPaymentMethodsGQL,
    private shopUserDeclarationGQL: ShopUserDeclarationGQL,
    private shopUserOrderItemGQL: ShopUserOrderItemGQL,
    private shopUserOrderRecreateGQL: ShopUserOrderRecreateGQL,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    if (!authHeader) throw new Error('authHeader Provider is required');
  }

  registerUser(input: IShopUserRegistrationVariables): Observable<IShopAuthToken> {
    return this.shopUserRegistrationGQL
      .mutate(input)
      .pipe(mapFetchResultData((r) => r.registerUser));
  }

  loginUser(input: IShopUserLoginVariables): Observable<IShopAuthToken> {
    return this.shopUserLoginGQL.mutate(input).pipe(mapFetchResultData((r) => r.loginUser));
  }

  createUserAddress(input: IShopCreateUserAddressVariables): Observable<IShopAddress> {
    return this.shopCreateUserAddressGQL
      .mutate(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCreateUserAddress));
  }

  updateUserAddress(input: IShopUpdateUserAddressVariables): Observable<IShopAddress> {
    return this.shopUpdateUserAddressGQL
      .mutate(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateUserAddress));
  }

  deleteUserAddress(input: IShopDeleteUserAddressVariables): Observable<{ id: number }> {
    return this.shopDeleteUserAddressGQL
      .mutate(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopDeleteUserAddress));
  }

  forgotUserPassword(input: IForgotPasswordMutationVariables): Observable<IForgotPassword> {
    return this.forgotPasswordGQL.mutate(input).pipe(mapFetchResultData((r) => r.forgotPassword));
  }

  resetUserPassword(input: IResetPasswordMutation, resetToken: string): Observable<IResetPassword> {
    return this.resetPasswordGQL
      .mutate(input, { context: { headers: { Authorization: resetToken } } })
      .pipe(mapFetchResultData((r) => r.resetPassword));
  }

  fetchAddresses(): Observable<IShopUserAddress[]> {
    return this.shopUserAddressesGQL
      .fetch(undefined, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopAuthUser.addresses));
  }

  refreshPaymentMethods(): Observable<IShopUserPaymentMethod[]> {
    return this.shopUserRefreshPaymentMethodsGQL
      .fetch(undefined, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopAuthUser.paymentMethods));
  }

  updateUser(input: IShopUpdateUserMutationVariables): Observable<IShopAuthUser> {
    return this.shopUpdateUserGQL
      .mutate(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateUser));
  }

  authUser(): Observable<IShopAuthUser> {
    return this.shopUserAuthGQL
      .fetch(undefined, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopAuthUser));
  }

  getUserOrders(query: IShopUserOrdersQuery): Observable<IShopCollection<IShopUserOrder>> {
    return this.shopUserOrdersGQL.fetch(query, this.authHeader.userAuthHeader).pipe(
      mapFetchResultData((r) => {
        return {
          items: r.items,
          totalItemsCount: r.itemsCount,
        };
      }),
    );
  }

  getUserOrderItems(input: TShopUserOrderItemQuery): Observable<TShopUserOrderItem[]> {
    return this.shopUserOrderItemGQL
      .fetch(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopOrder.items));
  }

  getUserOrderDetail(id: IShopOrderId): Observable<TShopUserOrderDetail> {
    return this.shopCheckoutOrderGQL
      .fetch({ id, locale: this.locale }, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopOrder));
  }

  updateUserPassword(input: ShopUpdateUserPasswordInput): Observable<{ id: number }> {
    return this.shopUpdateUserPasswordGQL
      .mutate({ input }, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateUserPassword));
  }

  archiveUserPaymentMethodGQL(
    query: IShopArchiveUserPaymentMethodVariables,
  ): Observable<IShopArchiveUserPayment> {
    return this.shopArchiveUserPaymentMethodGQL
      .mutate(query, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopArchiveUserPaymentMethod));
  }

  createUserPaymentMethod(
    input: IShopCreateUserPaymentMethodVariables,
  ): Observable<IShopUserPaymentMethod> {
    return this.shopCreateUserPaymentMethodGQL
      .mutate(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCreateUserPaymentMethod));
  }

  cancelUserPaymentMethod(
    input: IShopCancelUserPaymentMethodVariables,
  ): Observable<IShopUserPaymentMethod> {
    return this.shopCancelUserPaymentMethodGQL
      .mutate(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCancelUserPaymentMethod));
  }

  fetchUserPaymentMethod(
    input: IShopUserPaymentMethodVariables,
  ): Observable<IShopUserPaymentMethod> {
    return this.shopUserPaymentMethodGQL
      .fetch(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUserPaymentMethod));
  }

  refreshUserToken(input?: IShopRefreshUserMutationVariables): Observable<IShopAuthToken> {
    return this.shopRefreshUserGQL
      .mutate(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.refreshUser));
  }

  updateUserDeclaration(
    args: TShopUserDeclarationMutationVariables,
  ): Observable<TShopUserAgreement[]> {
    return this.shopUserDeclarationGQL
      .mutate(args, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUserDeclaration));
  }

  reCreateOrder(input: TShopUserOrderRecreateMutation): Observable<IShopUserOrderRecreate> {
    return this.shopUserOrderRecreateGQL
      .mutate(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopRecreateCart));
  }
}
